import {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
	useMemo,
	useCallback,
} from 'react';

import {ESharedEvents, useSharedEvent} from './SharedEventProvider';

export enum WOFSlice {
	NoPrize,
	Prize1,
	Prize2,
	Prize3,
}

interface SharedConfig {
	prize1Probability: number;
	prize2Probability: number;
	prize3Probability: number;
	noPrizeProbability: number;
}

interface SharedConfigContextProps {
	sharedConfig: SharedConfig;
	updateSharedConfig: (config: SharedConfig) => void;
	randomWin: () => WOFSlice;
}

interface SharedConfigProviderProps {
	children: ReactNode;
}

const defaultConfig: SharedConfig = {
	prize1Probability: 0,
	prize2Probability: 0,
	prize3Probability: 0,
	noPrizeProbability: 1,
};

const SharedConfigContext = createContext<SharedConfigContextProps | undefined>(
	undefined
);

export const SharedConfigProvider = ({children}: SharedConfigProviderProps) => {
	const {sendEvent, subscribe, isInitialized} = useSharedEvent();

	const [sharedConfig, setSharedConfig] =
		useState<SharedConfig>(defaultConfig);

	const updateSharedConfig = useCallback(
		(config: SharedConfig) => {
			setSharedConfig(config);
			sendEvent(ESharedEvents.SharedConfigUpdateEvent, config);
		},
		[sendEvent]
	);

	useEffect(() => {
		if (isInitialized) {
			console.log('Requesting initial configuration');
			sendEvent(ESharedEvents.RequestConfigEvent, undefined, true);
		}
	}, [isInitialized, sendEvent]);

	useEffect(() => {
		const unsubscribe = subscribe(
			ESharedEvents.SharedConfigUpdateEvent,
			(config: SharedConfig) => {
				if (
					typeof config.prize1Probability === 'number' &&
					typeof config.prize2Probability === 'number' &&
					typeof config.prize3Probability === 'number' &&
					typeof config.noPrizeProbability === 'number'
				) {
					console.log(
						`Updating shared config because of remote change`
					);
					console.log(config);
					setSharedConfig(config);
				}
			}
		);

		return () => {
			unsubscribe();
		};
	}, [subscribe]);

	const randomWin = useCallback(() => {
		const totalWeight =
			sharedConfig.noPrizeProbability +
			sharedConfig.prize1Probability +
			sharedConfig.prize2Probability +
			sharedConfig.prize3Probability;
		const randomValue = Math.random() * totalWeight;

		if (totalWeight === 0) {
			return WOFSlice.NoPrize;
		}

		if (randomValue < sharedConfig.noPrizeProbability) {
			return WOFSlice.NoPrize;
		} else if (
			randomValue <
			sharedConfig.noPrizeProbability + sharedConfig.prize1Probability
		) {
			return WOFSlice.Prize1;
		} else if (
			randomValue <
			sharedConfig.noPrizeProbability +
				sharedConfig.prize1Probability +
				sharedConfig.prize2Probability
		) {
			return WOFSlice.Prize2;
		} else {
			return WOFSlice.Prize3;
		}
	}, [
		sharedConfig.noPrizeProbability,
		sharedConfig.prize1Probability,
		sharedConfig.prize2Probability,
		sharedConfig.prize3Probability,
	]);

	const value = useMemo(() => {
		return {sharedConfig, updateSharedConfig, randomWin};
	}, [randomWin, sharedConfig, updateSharedConfig]);

	return (
		<SharedConfigContext.Provider value={value}>
			{children}
		</SharedConfigContext.Provider>
	);
};

export const useSharedConfig = (): SharedConfigContextProps => {
	const context = useContext(SharedConfigContext);
	if (!context) {
		throw new Error(
			'useSharedConfig must be used within a SharedConfigProvider'
		);
	}
	return context;
};
